/**
 * Change Templates Page
 * List, create, and edit reusable change templates.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Add,
  Edit,
  DocumentText,
  TickCircle,
  Warning2,
  ArrowLeft,
} from 'iconsax-react';
import {
  getTemplates,
  getTeams,
  ChangeTemplate,
  Team,
  ChangeCategory,
  RiskLevel,
} from '../../services/changeManagementApi';
import toast from 'react-hot-toast';
import { useRBAC } from '../../contexts/RBACContext';

const ChangeTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { canAccess } = useRBAC();

  const [templates, setTemplates] = useState<ChangeTemplate[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const canCreateOrEdit = canAccess('changes_manage') || canAccess('changes_edit');

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, teamsData] = await Promise.all([
        getTemplates({
          active_only: showOnlyActive,
          team: teamFilter || undefined,
          category: categoryFilter || undefined,
        }),
        getTeams(),
      ]);
      setTemplates(templatesData);
      setTeams(teamsData);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyActive, teamFilter, categoryFilter]);

  const handleCreate = () => {
    if (!canCreateOrEdit) {
      toast.error('You do not have permission to manage templates');
      return;
    }
    navigate('/change-templates/new');
  };

  const handleEdit = (template: ChangeTemplate) => {
    if (!canCreateOrEdit) {
      toast.error('You do not have permission to edit templates');
      return;
    }
    navigate(`/change-templates/${template.id}/edit`);
  };

  const getTeamName = (teamId?: string) => {
    if (!teamId) return 'All Teams';
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  const categoryOptions: ChangeCategory[] = [
    'infrastructure',
    'rule',
    'integration',
    'security',
    'network',
    'application',
    'configuration',
    'other',
  ];

  const riskOptions: RiskLevel[] = ['low', 'medium', 'high', 'critical'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/change-management')}
            className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors"
            aria-label="Back to Change Management"
          >
            <ArrowLeft size={20} color="#9CA3AF" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Change Templates</h1>
            <p className="text-gray-400 mt-1">
              Reusable blueprints for common change requests
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="px-3 py-2 rounded-lg bg-background-card border border-border-dark text-gray-300 hover:border-primary transition-colors"
          >
            Refresh
          </button>
          {canCreateOrEdit && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors"
            >
              <Add size={20} color="#FFFFFF" />
              <span>New Template</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background-card rounded-xl p-4 border border-border-dark flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Team</label>
          <select
            value={teamFilter}
            onChange={e => setTeamFilter(e.target.value)}
            className="px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary min-w-[200px]"
          >
            <option value="">All Teams</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">Category</label>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary min-w-[200px]"
          >
            <option value="">All Categories</option>
            {categoryOptions.map(cat => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="active-only"
            type="checkbox"
            checked={showOnlyActive}
            onChange={e => setShowOnlyActive(e.target.checked)}
            className="w-4 h-4 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
          />
          <label htmlFor="active-only" className="text-sm text-gray-300">
            Show only active templates
          </label>
        </div>
      </div>

      {/* Templates Table / Grid */}
      <div className="bg-background-card rounded-xl p-4 border border-border-dark">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <DocumentText size={40} color="#4B5563" style={{ margin: '0 auto 12px' }} />
            <p className="text-lg mb-2">No templates found</p>
            <p className="text-sm">
              {canCreateOrEdit
                ? 'Create a new template to standardize common change requests.'
                : 'No templates are available. Contact an administrator to configure templates.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(template => (
              <div
                key={template.id}
                className="p-4 rounded-lg bg-background-hover border border-border-dark hover:border-primary/60 transition-colors flex items-start justify-between gap-4"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <DocumentText size={24} color="#60A5FA" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold text-sm md:text-base">
                        {template.template_name}
                      </h3>
                      {template.auto_approve && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                          <TickCircle size={14} /> Auto-approve
                        </span>
                      )}
                      {!template.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
                          <Warning2 size={14} /> Inactive
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-gray-400 text-sm mb-1">{template.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                      <span className="px-2 py-1 rounded-full bg-slate-700/60">
                        Team: {getTeamName(template.team)}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-slate-700/60 capitalize">
                        Category: {template.category}
                      </span>
                      {template.risk_level && (
                        <span className="px-2 py-1 rounded-full bg-slate-700/60 capitalize">
                          Risk: {template.risk_level}
                        </span>
                      )}
                      {typeof template.usage_count === 'number' && (
                        <span className="px-2 py-1 rounded-full bg-slate-700/60">
                          Used {template.usage_count} time
                          {template.usage_count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {canCreateOrEdit && (
                  <button
                    onClick={() => handleEdit(template)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-card border border-border-dark text-gray-300 hover:border-primary hover:text-primary transition-colors text-sm"
                  >
                    <Edit size={16} />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend / Info */}
      <div className="text-xs text-gray-500 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <TickCircle size={14} color="#34D399" />
          <span>Auto-approve templates may skip some approval steps for low-risk changes.</span>
        </div>
      </div>
    </div>
  );
};

export default ChangeTemplatesPage;

